/**
 * Client-side working-time math — mirror of api/src/lib/workingTime.ts.
 *
 * The countdown cell needs to display a deadline without a round trip on
 * every re-render.  This module hydrates the calendar once via
 * useCalendar() and does all its arithmetic locally.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface CalendarPayload {
  calendarId: number;
  name: string;
  timeZone: string;
  isDefault: boolean;
  days: { dayOfWeek: number; startTime: string; endTime: string; isWorking: boolean }[];
  breaks: { dayOfWeek: number; startTime: string; endTime: string; enabled: boolean }[];
  holidays: string[]; // YYYY-MM-DD
}

interface Cal {
  days: { dayOfWeek: number; startMin: number; endMin: number; isWorking: boolean }[];
  breaks: { dayOfWeek: number; startMin: number; endMin: number }[];
  holidays: Set<string>;
}

// The safety default matches the SQL migration seed (Mon–Sat 09–18, lunch
// 13:30–14, Sunday off) so the countdown still renders sanely when the
// API hasn't loaded yet.
const DEFAULT: Cal = {
  days: [0, 1, 2, 3, 4, 5, 6].map((d) => ({
    dayOfWeek: d,
    startMin: d === 0 ? 0 : 9 * 60,
    endMin: d === 0 ? 0 : 18 * 60,
    isWorking: d !== 0,
  })),
  breaks: [1, 2, 3, 4, 5, 6].map((d) => ({
    dayOfWeek: d,
    startMin: 13 * 60 + 30,
    endMin: 14 * 60,
  })),
  holidays: new Set(),
};

export function useCalendar() {
  return useQuery({
    queryKey: ['working-calendar'],
    queryFn: () => api<CalendarPayload>('/api/settings/calendar'),
    staleTime: 5 * 60_000,
  });
}

export function hydrate(payload: CalendarPayload | undefined): Cal {
  if (!payload) return DEFAULT;
  return {
    days: payload.days.map((d) => ({
      dayOfWeek: d.dayOfWeek,
      startMin: toMin(d.startTime),
      endMin: toMin(d.endTime),
      isWorking: d.isWorking,
    })),
    breaks: payload.breaks
      .filter((b) => b.enabled)
      .map((b) => ({
        dayOfWeek: b.dayOfWeek,
        startMin: toMin(b.startTime),
        endMin: toMin(b.endTime),
      })),
    holidays: new Set(payload.holidays),
  };
}

// ---- arithmetic (kept in step with api/src/lib/workingTime.ts) ----

export function addWorkingHours(cal: Cal, from: Date, hours: number): Date {
  let remaining = hours * 60;
  const cursor = new Date(from);
  let safety = 400;
  while (remaining > 0 && safety-- > 0) {
    const windows = windowsFor(cal, cursor);
    if (windows.length === 0) {
      cursor.setDate(cursor.getDate() + 1);
      cursor.setHours(0, 0, 0, 0);
      continue;
    }
    const nowMin = cursor.getHours() * 60 + cursor.getMinutes();
    let spent = 0;
    for (const [ws, we] of windows) {
      if (we <= nowMin && spent === 0) continue;
      const startAt = Math.max(ws, spent === 0 ? nowMin : ws);
      const capacity = we - startAt;
      if (capacity <= 0) continue;
      if (remaining <= capacity) {
        cursor.setHours(0, 0, 0, 0);
        cursor.setMinutes(startAt + remaining);
        return cursor;
      }
      remaining -= capacity;
      spent = we;
    }
    cursor.setDate(cursor.getDate() + 1);
    cursor.setHours(0, 0, 0, 0);
  }
  return cursor;
}

export function windowsFor(cal: Cal, date: Date): [number, number][] {
  if (cal.holidays.has(fmt(date))) return [];
  const day = cal.days.find((d) => d.dayOfWeek === date.getDay());
  if (!day || !day.isWorking || day.startMin === day.endMin) return [];
  const brk = cal.breaks.filter((b) => b.dayOfWeek === date.getDay());
  return subtract([[day.startMin, day.endMin]], brk.map((b) => [b.startMin, b.endMin]));
}

function subtract(bases: [number, number][], subs: [number, number][]): [number, number][] {
  let result = bases.slice();
  for (const [ss, se] of subs) {
    const next: [number, number][] = [];
    for (const [bs, be] of result) {
      if (se <= bs || ss >= be) { next.push([bs, be]); continue; }
      if (ss > bs) next.push([bs, ss]);
      if (se < be) next.push([se, be]);
    }
    result = next;
  }
  return result.filter(([a, b]) => b > a);
}

function toMin(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function fmt(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
